/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import {
  getTariffs,
  getTariffsProperties,
} from "../../lib/Queries";
import { TariffCard } from "../Tariff";
import type { BillConfigResponse } from "../../lib/Types";

interface TariffDetails {
  tariffCode?: string;
  tariffName?: string;
  lseId?: number;
  effectiveDate?: string;
  endDate?: string | null;
}

interface RecalculateReportModalProps {
  open: boolean;
  onClose: () => void;
  onRecalculate: () => void;
  preConfig: BillConfigResponse | null;
  postConfig: BillConfigResponse | null;
  billingCycleStart?: string;
  billingCycleEnd?: string;
  assignedMonth?: string;
  isRecalculating?: boolean;
}

const RecalculateReportModal: React.FC<RecalculateReportModalProps> = ({
  open,
  onClose,
  onRecalculate,
  preConfig,
  postConfig,
  billingCycleStart,
  billingCycleEnd,
  assignedMonth,
  isRecalculating = false,
}) => {
  const [tariffData, setTariffData] = useState<{
    pre: TariffDetails | null;
    post: TariffDetails | null;
  }>({ pre: null, post: null });
  const [tariffPropertiesCounts, setTariffPropertiesCounts] = useState<{
    pre: number;
    post: number;
  }>({ pre: 0, post: 0 });
  const [propertiesDisplayNames, setPropertiesDisplayNames] = useState<{
    pre: Record<string, string>;
    post: Record<string, string>;
  }>({ pre: {}, post: {} });
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);

  useEffect(() => {
    const fetchTariffDetails = async () => {
      if (!open || (!preConfig && !postConfig)) return;

      setIsLoadingTariffs(true);
      try {
        const tariffPromises: Promise<{ type: "pre" | "post"; data: any }>[] =
          [];
        const propertiesPromises: Promise<{
          type: "pre" | "post";
          data: any;
        }>[] = [];

        if (preConfig?.master_tariff_id) {
          tariffPromises.push(
            getTariffs(String(preConfig.master_tariff_id))
              .then((data) => ({ type: "pre" as const, data }))
              .catch(() => ({ type: "pre" as const, data: null })),
          );
          propertiesPromises.push(
            getTariffsProperties(String(preConfig.master_tariff_id))
              .then((data) => ({ type: "pre" as const, data }))
              .catch(() => ({ type: "pre" as const, data: null })),
          );
        }

        if (postConfig?.master_tariff_id) {
          tariffPromises.push(
            getTariffs(String(postConfig.master_tariff_id))
              .then((data) => ({ type: "post" as const, data }))
              .catch(() => ({ type: "post" as const, data: null })),
          );
          propertiesPromises.push(
            getTariffsProperties(String(postConfig.master_tariff_id))
              .then((data) => ({ type: "post" as const, data }))
              .catch(() => ({ type: "post" as const, data: null })),
          );
        }

        const [tariffResults, propertiesResults] = await Promise.all([
          Promise.all(tariffPromises),
          Promise.all(propertiesPromises),
        ]);

        const newTariffData: { pre: TariffDetails | null; post: TariffDetails | null } = {
          pre: null,
          post: null,
        };
        const newPropertiesCounts = { pre: 0, post: 0 };
        const newDisplayNames: {
          pre: Record<string, string>;
          post: Record<string, string>;
        } = { pre: {}, post: {} };

        tariffResults.forEach((result) => {
          if (result.data?.results?.length > 0) {
            const tariff =
              result.data.results.find(
                (t: TariffDetails) => t?.endDate == null,
              ) ||
              result.data.results[result.data.results.length - 1];
            newTariffData[result.type] = tariff;
          }
        });

        propertiesResults.forEach((result) => {
          if (result.data) {
            newPropertiesCounts[result.type] = result.data.count || 0;
            const mapping: Record<string, string> = {};
            if (result.data.results) {
              result.data.results.forEach(
                (prop: { keyName?: string; displayName?: string }) => {
                  if (prop.keyName) {
                    mapping[prop.keyName] = prop.displayName || prop.keyName;
                  }
                },
              );
            }
            newDisplayNames[result.type] = mapping;
          }
        });

        setTariffData(newTariffData);
        setTariffPropertiesCounts(newPropertiesCounts);
        setPropertiesDisplayNames(newDisplayNames);
      } catch (error) {
        console.error("Error fetching tariff details:", error);
      } finally {
        setIsLoadingTariffs(false);
      }
    };

    if (open) {
      fetchTariffDetails();
    }
  }, [open, preConfig, postConfig]);

  const showLabel = !!preConfig && !!postConfig;
  const hasSiblingCard = !!preConfig && !!postConfig;

  const startFormatted = billingCycleStart
    ? dayjs(billingCycleStart).format("MMM D")
    : "";
  const endFormatted = billingCycleEnd
    ? dayjs(billingCycleEnd).format("MMM D YYYY")
    : "";
  const billingPeriodDisplay =
    startFormatted && endFormatted
      ? `${startFormatted} - ${endFormatted}`
      : "";

  const assignedMonthDisplay = assignedMonth
    ? dayjs(assignedMonth).format("MMMM YYYY")
    : "";

  return (
    <Dialog
      open={open}
      onClose={isRecalculating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          p: 4,
          borderRadius: "4px",
          boxShadow: "0px 4px 18px 0px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              lineHeight: 1.334,
              color: "text.primary",
            }}
          >
            Recalculate Report
          </Typography>

          {/* Tariff Cards */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: hasSiblingCard ? "nowrap" : "wrap",
            }}
          >
            {preConfig && (
              <TariffCard
                config={preConfig}
                label={showLabel ? "Pre-Project Tariff" : "Tariff"}
                tariffDetails={tariffData.pre}
                totalPropertiesCount={tariffPropertiesCounts.pre}
                displayNameMap={propertiesDisplayNames.pre}
                isLoading={isLoadingTariffs}
                showLabel
                hasSiblingCard={hasSiblingCard}
              />
            )}
            {postConfig && (
              <TariffCard
                config={postConfig}
                label="Post-Project Tariff"
                tariffDetails={tariffData.post}
                totalPropertiesCount={tariffPropertiesCounts.post}
                displayNameMap={propertiesDisplayNames.post}
                isLoading={isLoadingTariffs}
                showLabel
                hasSiblingCard={hasSiblingCard}
              />
            )}
          </Box>

          {/* Billing Period + Assigned Month + Warning */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {billingPeriodDisplay && (
              <Box
                sx={{
                  backgroundColor: "grey.50",
                  borderRadius: "4px",
                  p: 3,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Billing Period
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {billingPeriodDisplay}
                </Typography>
              </Box>
            )}

            {assignedMonthDisplay && (
              <Box
                sx={{
                  backgroundColor: "grey.50",
                  borderRadius: "4px",
                  p: 3,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Assign to Month
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {assignedMonthDisplay}
                </Typography>
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                letterSpacing: "0.17px",
              }}
            >
              If there is a report for this month already, this will overwrite
              it.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={isRecalculating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onRecalculate}
              disabled={isRecalculating}
              startIcon={
                isRecalculating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
            >
              {isRecalculating ? "Recalculating…" : "Recalculate"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RecalculateReportModal;
